import { useSearchParams } from 'react-router-dom'
import {
  useMetricsSummary,
  useMetricsTimeseries,
  useTopCampaigns,
  useBudgetMonitor,
} from '@/api/metrics'
import { usePromotionStats } from '@/api/promotions'
import KpiCard from '@/components/KpiCard'
import MetricsChart from '@/components/MetricsChart'
import TopCampaignsTable from '@/components/TopCampaignsTable'
import BudgetMonitor from '@/components/BudgetMonitor'
import Card from '@/components/Card'
import SectionHead from '@/components/SectionHead'
import RecommendationsPanel from '@/components/RecommendationsPanel'
import DateRangePicker from '@/components/DateRangePicker'
import {
  defaultRange,
  diffDays,
  isoDaysAgo,
  isoToday,
  useDateRange,
  type DateRange,
} from '@/lib/dateRange'
import {
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Eye,
  MousePointerClick,
  Target,
  BarChart3,
  Percent,
  ArrowUpRight,
  Download,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type AdTypeFilter = 'all' | 'sp' | 'sb' | 'sd'

export function parseAdType(value: string | null): AdTypeFilter {
  return value === 'sp' || value === 'sb' || value === 'sd' || value === 'all' ? value : 'all'
}

const QUICK_RANGE_OPTIONS = [
  { value: 7, label: '7 T' },
  { value: 14, label: '14 T' },
  { value: 30, label: '30 T' },
  { value: 60, label: '60 T' },
] as const

// Erkennt, ob aktueller Range einem Quick-Preset entspricht (für Active-Highlight).
// Liefert die Tagesanzahl (7/14/30/60) bei Treffer, sonst null.
function matchPreset(r: DateRange): number | null {
  if (r.to !== isoToday()) return null
  for (const opt of QUICK_RANGE_OPTIONS) {
    if (r.from === isoDaysAgo(opt.value)) return opt.value
  }
  return null
}

type ToggleOption<T extends string | number> = { value: T; label: string }

function Toggle<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<ToggleOption<T>>
  value: T | null
  onChange: (next: T) => void
}) {
  return (
    <div className="inline-flex rounded-lg border border-[var(--border)] bg-[var(--muted)] p-0.5">
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
              active
                ? 'bg-[var(--card)] text-[var(--foreground)] shadow-sm'
                : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

const AD_TYPE_OPTIONS: ReadonlyArray<ToggleOption<AdTypeFilter>> = [
  { value: 'all', label: 'Alle' },
  { value: 'sp', label: 'SP' },
  { value: 'sb', label: 'SB' },
  { value: 'sd', label: 'SD' },
]

// v29 Phase C: 3 statische Aktions-Boxen entfernt — durch <RecommendationsPanel/> ersetzt,
// gespeist vom /recommendations/dashboard/-Endpoint (4 Rule-Funktionen).

export default function Dashboard() {
  const [params, setParams] = useSearchParams()
  const { range, setRange } = useDateRange({ fallbackDays: 7 })
  const adType = parseAdType(params.get('adType'))

  function setAdType(next: AdTypeFilter) {
    const newParams = new URLSearchParams(params)
    if (next === 'all') newParams.delete('adType')
    else newParams.set('adType', next)
    setParams(newParams, { replace: true })
  }

  const filters = {
    dateFrom: range.from,
    dateTo: range.to,
    adType: adType === 'all' ? undefined : adType,
  }

  const { data: summaryData, isLoading: summaryLoading } = useMetricsSummary(filters)
  const { data: timeseriesData, isLoading: timeseriesLoading } = useMetricsTimeseries(filters)
  const { data: topData, isLoading: topLoading } = useTopCampaigns({ ...filters, limit: 10 })
  const { data: budgetData, isLoading: budgetLoading } = useBudgetMonitor()
  const { data: promoData } = usePromotionStats()

  const summary = summaryData?.summary
  const timeseries = timeseriesData?.timeseries ?? []
  const topCampaigns = topData?.campaigns ?? []
  const budgetAlerts = budgetData?.alerts ?? []
  const promoStats = promoData?.stats

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">
            Performance-Übersicht
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Amazon Advertising Performance
            {promoStats && promoStats.pending > 0 && (
              <span className="ml-2 font-semibold text-amber-600">
                {promoStats.pending} Promotions warten auf Approval
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Toggle
            options={QUICK_RANGE_OPTIONS}
            value={matchPreset(range)}
            onChange={(days) => setRange(defaultRange(days))}
          />
          <DateRangePicker value={range} onChange={setRange} />
          <Toggle options={AD_TYPE_OPTIONS} value={adType} onChange={setAdType} />
          <button
            type="button"
            disabled
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white opacity-60"
            title="Export folgt in Phase 7"
          >
            <Download className="h-3 w-3" />
            Export
          </button>
        </div>
      </div>

      {/* KPI Row 1 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard title="Spend" value={summary?.spend ?? 0} delta={summary?.spendDelta} format="currency" invertDelta icon={DollarSign} loading={summaryLoading} />
        <KpiCard title="Sales" value={summary?.sales ?? 0} delta={summary?.salesDelta} format="currency" icon={TrendingUp} loading={summaryLoading} />
        <KpiCard title="ACoS" value={summary?.acos ?? 0} delta={summary?.acosDelta} format="percent" invertDelta icon={Target} loading={summaryLoading} />
        <KpiCard title="ROAS" value={summary?.roas ?? 0} delta={summary?.roasDelta} format="decimal" icon={BarChart3} loading={summaryLoading} />
        <KpiCard title="Orders" value={summary?.orders ?? 0} delta={summary?.ordersDelta} format="number" icon={ShoppingCart} loading={summaryLoading} />
      </div>

      {/* KPI Row 2 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard title="Impressions" value={summary?.impressions ?? 0} delta={summary?.impressionsDelta} format="number" icon={Eye} loading={summaryLoading} />
        <KpiCard title="Clicks" value={summary?.clicks ?? 0} delta={summary?.clicksDelta} format="number" icon={MousePointerClick} loading={summaryLoading} />
        <KpiCard title="CPC" value={summary?.cpc ?? 0} delta={summary?.cpcDelta} format="currency" invertDelta icon={DollarSign} loading={summaryLoading} />
        <KpiCard title="CTR" value={summary?.ctr ?? 0} delta={summary?.ctrDelta} format="percent" icon={Percent} loading={summaryLoading} />
        <KpiCard title="CVR" value={summary?.cvr ?? 0} delta={summary?.cvrDelta} format="percent" icon={ArrowUpRight} loading={summaryLoading} />
      </div>

      {/* Trend Chart */}
      <Card className="p-4">
        <SectionHead
          title="Spend · Sales · ACoS-Trend"
          subtitle={`Tageswerte · ${range.from} → ${range.to} (${diffDays(range)} T)`}
          right={
            <div className="flex items-center gap-3 text-[11.5px] text-[var(--muted-foreground)]">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-[3px] w-3 rounded-sm bg-orange-500" /> Spend
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-[3px] w-3 rounded-sm bg-emerald-500" /> Sales
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-px w-3 border-t-2 border-dashed border-indigo-500" /> ACoS
              </span>
            </div>
          }
        />
        <MetricsChart data={timeseries} loading={timeseriesLoading} />
      </Card>

      {/* Bottom Grid: Top-Campaigns + Action-Boxes (+ Budget) */}
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <TopCampaignsTable campaigns={topCampaigns} loading={topLoading} />
        </div>
        <div className="flex flex-col gap-3 lg:col-span-2">
          <RecommendationsPanel />
          {budgetAlerts.length > 0 && (
            <BudgetMonitor alerts={budgetAlerts} loading={budgetLoading} />
          )}
        </div>
      </div>
    </div>
  )
}
